import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { Search, Copy, Filter, Hash, EyeOff, Eye } from "lucide-react";

/**
 * AgentAvailability
 * Props:
 *  - agentUid: string (required if cards not provided)
 *  - cards: optional pre-fetched cards array [{ id, filled, locked, status }, ...]
 *  - statuses: optional array of statuses to include (default: ["active"])
 */
export default function AgentAvailability({
  agentUid,
  cards: externalCards,
  statuses = ["active"],
}) {
  const [cards, setCards] = useState([]);
  const [search, setSearch] = useState("");
  const [hideZeros, setHideZeros] = useState(false);

  // Subscribe to cards only if not provided by parent
  useEffect(() => {
    if (externalCards) {
      setCards(externalCards);
      return;
    }
    if (!agentUid) return;

    const qy = query(
      collection(db, "cards"),
      where("agentUid", "==", agentUid),
      where("status", "in", statuses.length ? statuses : ["active"]),
      orderBy("createdAt", "desc")
    );

    const un = onSnapshot(
      qy,
      (snap) => {
        const arr = [];
        snap.forEach((d) => arr.push({ id: d.id, ...d.data() }));
        setCards(arr);
      },
      (err) => toast.error(`Availability stream failed: ${err.message}`)
    );
    return () => un();
  }, [agentUid, externalCards, statuses]);

  // Build availability counts for 00..99
  const { counts, totalCards } = useMemo(() => {
    const avail = Array(100).fill(0);
    let total = 0;
    for (const c of cards) {
      // Ensure we only count intended statuses
      if (statuses.length && !statuses.includes(c.status)) continue;
      total += 1;
      const filled = new Set(c.filled || []);
      const locked = new Set(c.locked || []);
      for (let n = 0; n < 100; n++) {
        if (!filled.has(n) && !locked.has(n)) {
          avail[n] += 1;
        }
      }
    }
    return { counts: avail, totalCards: total };
  }, [cards, statuses]);

  // Parse search: accepts “35” or “12, 24 30”
  const searchSet = useMemo(() => {
    const s = new Set();
    const raw = String(search).trim();
    if (!raw) return s;
    for (const piece of raw.split(/[^\d]+/g)) {
      if (!piece) continue;
      const n = Number(piece);
      if (Number.isInteger(n) && n >= 0 && n <= 99) s.add(n);
    }
    return s;
  }, [search]);

  const rows = useMemo(() => {
    const list = [];
    for (let n = 0; n < 100; n++) {
      const cnt = counts[n];
      if (hideZeros && cnt === 0) continue;
      if (searchSet.size && !searchSet.has(n)) continue;
      list.push({ n, cnt });
    }
    return list;
  }, [counts, hideZeros, searchSet]);

  // Copy helpers
  const fmtLines = (arr) =>
    arr.map(({ n, cnt }) => `${String(n).padStart(2, "0")}-${cnt}`).join("\n");

  const copyVisible = async () => {
    try {
      await navigator.clipboard.writeText(fmtLines(rows));
      toast.success("Copied visible availability");
    } catch {
      toast.error("Copy failed");
    }
  };
  const copyAll = async () => {
    try {
      const every = Array.from({ length: 100 }, (_, n) => ({
        n,
        cnt: counts[n],
      }));
      await navigator.clipboard.writeText(fmtLines(every));
      toast.success("Copied availability for 00–99");
    } catch {
      toast.error("Copy failed");
    }
  };

  // Color scale by % availability
  const badgeClass = (cnt) => {
    if (totalCards === 0) return "bg-slate-600 text-white";
    const pct = (cnt / totalCards) * 100;
    if (pct >= 90) return "bg-emerald-500/20 text-emerald-300";
    if (pct >= 70) return "bg-lime-500/20 text-lime-300";
    if (pct >= 40) return "bg-amber-500/20 text-amber-300";
    return "bg-rose-500/20 text-rose-300";
  };

  return (
    <section className="mt-8">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Hash className="w-4 h-4 text-lime-300" />
          <h2 className="text-lg font-semibold">
            Number availability (across your cards)
          </h2>
        </div>
        <div className="text-xs text-slate-400">
          Total cards counted: <b>{totalCards}</b>
        </div>
      </div>

      <div className="p-4 bg-slate-800 rounded-2xl ring-1 ring-slate-700">
        {/* Controls */}
        <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-slate-900 text-white ring-1 ring-slate-700"
              placeholder="Search numbers (e.g., 35 or 12,24,30)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setHideZeros((v) => !v)}
              className="px-3 py-2 rounded-xl bg-slate-700 text-white text-sm flex items-center gap-2"
              title="Toggle zero-count rows"
            >
              {hideZeros ? <EyeOff size={16} /> : <Eye size={16} />}
              {hideZeros ? "Show zeros" : "Hide zeros"}
            </button>

            <button
              onClick={copyVisible}
              className="px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold text-sm flex items-center gap-2"
              title="Copy visible rows"
            >
              <Copy size={16} />
              Copy visible
            </button>
            <button
              onClick={copyAll}
              className="px-3 py-2 rounded-xl bg-slate-700 text-white text-sm flex items-center gap-2"
              title="Copy 00–99"
            >
              <Filter size={16} />
              Copy 00–99
            </button>
          </div>
        </div>

        {/* Grid */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
          {rows.map(({ n, cnt }) => (
            <div
              key={n}
              className={`flex items-center justify-between px-3 py-2 rounded-xl ring-1 ring-slate-700 bg-slate-900`}
            >
              <span className="font-mono text-sm text-slate-200">
                {String(n).padStart(2, "0")}
              </span>
              <span
                className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeClass(
                  cnt
                )}`}
              >
                {cnt}
              </span>
            </div>
          ))}
          {!rows.length && (
            <div className="col-span-full text-slate-300 text-sm">
              No numbers match your filter.
            </div>
          )}
        </div>

        {/* Tiny legend */}
        <div className="mt-3 text-[11px] text-slate-400">
          Count = how many of your counted cards still have that number
          available. Copy buttons export in the format <code>NN-COUNT</code>{" "}
          (e.g. <code>35-100</code>).
        </div>
      </div>
    </section>
  );
}
